import React, {useState} from 'react';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {
    Plus,
    Building2,
    Users,
    AlertTriangle,
    Edit2,
    Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {supabase} from '@/services/supabase';
import {
    Button,
    Input,
    Select,
    Table,
    Modal,
    LoadingSpinner,
    Badge,
    FilterBar
} from '@/components/ui';
import type {
    Organization,
    CreateOrganizationData,
    UpdateOrganizationData,
    OrganizationFilters,
} from '@/types';
import {US_STATES} from '@/constants';

// Organization with its child accounts (Tier-2 / Schools) embedded, for the
// "Schools" column count/list.
type OrganizationWithAccounts = Organization & {
    accounts?: { id: string; name: string }[];
};
import {formatDate, isExpired} from "@/utils/formatters";
import {applyOrganizationFilters, resolveSchoolOrganizationId} from '@/utils/organizationFilters';
import {buildOrganizationFilters} from '@/config/listFilters';
import {sortRows} from '@/utils/sorting';
import {useAccountType} from '@/context/AccountTypeContext';

interface FormData {
    name: string;
    state: string;
    administrator_name: string;
    administrator_email: string;
    administrator_phone: string;
    active_activation_date: string;
    account_expiration_date: string;
}

const initialFormData: FormData = {
    name: '',
    state: '',
    administrator_name: '',
    administrator_email: '',
    administrator_phone: '',
    active_activation_date: '',
    account_expiration_date: ''
};

export const ManageOrganizations: React.FC = () => {
    const queryClient = useQueryClient();
    const {labels, accountType} = useAccountType();

    // State management
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [editingOrganization, setEditingOrganization] = useState<Organization | null>(null);
    const [formData, setFormData] = useState<FormData>(initialFormData);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [filters, setFilters] = useState<OrganizationFilters>({
        account_type: accountType
    });
    const [schoolFilter, setSchoolFilter] = useState<string>(''); // holds an account id
    const [sortBy, setSortBy] = useState<string>('name');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

    // Update filters when account type changes
    React.useEffect(() => {
        setFilters(prev => ({...prev, account_type: accountType}));
    }, [accountType]);

    // Accounts (Tier-2 / Schools) for the "filter districts by school" dropdown.
    const {data: accountsForFilter = []} = useQuery({
        queryKey: ['accounts-for-org-filter', accountType],
        queryFn: async (): Promise<{ id: string; name: string; organization_id: string }[]> => {
            const {data, error} = await supabase
                .from('accounts')
                .select('id, name, organization_id')
                .eq('account_type', accountType)
                .order('name');
            if (error) throw error;
            return data || [];
        },
        staleTime: 60 * 1000,
    });

    // Fetch organizations with filters
    const {
        data: organizations,
        isLoading,
    } = useQuery({
        queryKey: ['organizations', filters, searchTerm, sortBy, sortOrder, schoolFilter],
        queryFn: async (): Promise<OrganizationWithAccounts[]> => {
            let query = supabase
                .from('organizations')
                .select('*, accounts(id, name)')
                .eq('account_type', accountType);

            // Direct columns sort server-side; the Schools column is a child
            // count, so sort it client-side after fetching.
            if (sortBy !== 'schools') {
                query = query.order(sortBy, {ascending: sortOrder === 'asc'});
            }

            query = applyOrganizationFilters(query, {
                searchTerm,
                state: filters.state,
                status: filters.status,
                schoolOrgId: resolveSchoolOrganizationId(schoolFilter, accountsForFilter),
            });

            const {data, error} = await query;
            if (error) throw error;
            const rows = (data as OrganizationWithAccounts[]) || [];
            return sortBy === 'schools'
                ? sortRows(rows, sortOrder, (r) => r.accounts?.length || 0)
                : rows;
        },
        staleTime: 30 * 1000,
    });

    // Create organization mutation
    const createOrganizationMutation = useMutation({
        mutationFn: async (data: CreateOrganizationData): Promise<Organization> => {
            const {data: result, error} = await supabase
                .from('organizations')
                .insert(data)
                .select()
                .single();

            if (error) throw error;
            return result;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['organizations']});
            toast.success(`${labels.organizationSingular} created successfully`);
            handleCloseModal();
        },
        onError: (error: any) => {
            toast.error(`Failed to create ${labels.organizationSingular.toLowerCase()}: ${error.message}`);
        },
    });

    // Update organization mutation
    const updateOrganizationMutation = useMutation({
        mutationFn: async ({id, data}: { id: string; data: UpdateOrganizationData }): Promise<Organization> => {
            const {data: result, error} = await supabase
                .from('organizations')
                .update(data)
                .eq('id', id)
                .select()
                .single();

            if (error) throw error;
            return result;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['organizations']});
            toast.success(`${labels.organizationSingular} updated successfully`);
            handleCloseModal();
        },
        onError: (error: any) => {
            toast.error(`Failed to update ${labels.organizationSingular.toLowerCase()}: ${error.message}`);
        },
    });

    // Delete organization mutation
    const deleteOrganizationMutation = useMutation({
        mutationFn: async (id: string): Promise<void> => {
            const {error} = await supabase
                .from('organizations')
                .delete()
                .eq('id', id);

            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['organizations']});
            toast.success(`${labels.organizationSingular} deleted successfully`);
        },
        onError: (error: any) => {
            toast.error(`Failed to delete ${labels.organizationSingular.toLowerCase()}: ${error.message}`);
        },
    });

    // Helper functions
    const filteredCount = organizations?.length || 0;
    const expiredCount = organizations?.filter(org =>
        org.account_expiration_date && isExpired(org.account_expiration_date)
    ).length || 0;

    const handleSort = (column: string, order: 'asc' | 'desc'): void => {
        setSortBy(column);
        setSortOrder(order);
    };

    const handleOpenModal = (organization: Organization | null = null): void => {
        if (organization) {
            setEditingOrganization(organization);
            setFormData({
                name: organization.name,
                state: organization.state,
                administrator_name: organization.administrator_name,
                administrator_email: organization.administrator_email,
                administrator_phone: organization.administrator_phone || '',
                active_activation_date: organization.active_activation_date?.split('T')[0] || '',
                account_expiration_date: organization.account_expiration_date?.split('T')[0] || ''
            });
        } else {
            setEditingOrganization(null);
            setFormData(initialFormData);
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = (): void => {
        setIsModalOpen(false);
        setEditingOrganization(null);
        setFormData(initialFormData);
    };

    const handleInputChange = (field: keyof FormData, value: string): void => {
        // Normalize administrator_email to lowercase
        const normalizedValue = field === 'administrator_email'
            ? value.toLowerCase()
            : value;

        setFormData(prev => ({...prev, [field]: normalizedValue}));
    };

    const handleSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();

        const submitData: CreateOrganizationData = {
            name: formData.name.trim(),
            state: formData.state,
            administrator_name: formData.administrator_name.trim(),
            administrator_email: formData.administrator_email.trim(),
            administrator_phone: formData.administrator_phone.trim() || undefined,
            account_type: accountType,
            active_activation_date: formData.active_activation_date || undefined,
            account_expiration_date: formData.account_expiration_date || undefined
        };

        if (editingOrganization) {
            updateOrganizationMutation.mutate({
                id: editingOrganization.id,
                data: submitData
            });
        } else {
            createOrganizationMutation.mutate(submitData);
        }
    };

    const handleDelete = async (organization: Organization): Promise<void> => {
        if (window.confirm(`Are you sure you want to delete "${organization.name}"?`)) {
            deleteOrganizationMutation.mutate(organization.id);
        }
    };

    // Table columns configuration
    const columns = [
        {
            key: 'name',
            title: `${labels.organizationSingular} Name`,
            sortable: true,
            render: (value: string, record: Organization) => (
                <div className="flex items-start space-x-3">
                    <Building2 className="h-5 w-5 text-blue-500 mt-0.5"/>
                    <div>
                        <p className="font-medium text-gray-900">{value}</p>
                        <p className="text-sm text-gray-500">{record.state}</p>
                    </div>
                </div>
            ),
        },
        {
            key: 'schools',
            title: labels.accountPlural,
            sortable: true,
            render: (_: any, record: OrganizationWithAccounts) => {
                const schools = record.accounts ?? [];
                if (schools.length === 0) {
                    return <span className="text-sm text-gray-400">None</span>;
                }
                return (
                    <div>
                        <p className="font-medium text-gray-900">{schools.length}</p>
                        <p className="text-sm text-gray-500 truncate max-w-xs">
                            {schools.map((s) => s.name).join(', ')}
                        </p>
                    </div>
                );
            },
        },
        {
            key: 'administrator_name',
            title: 'Administrator',
            sortable: true,
            render: (value: string, record: Organization) => (
                <div>
                    <p className="font-medium text-gray-900">{value}</p>
                    <p className="text-sm text-gray-500">{record.administrator_email}</p>
                </div>
            ),
        },
        {
            key: 'status',
            title: 'Status',
            sortable: true,
            render: (value: string, record: Organization) => {
                const expired = record.account_expiration_date && isExpired(record.account_expiration_date);
                return (
                    <div className="space-y-1">
                        <Badge variant={value === 'active' && !expired ? 'success' : 'default'}>
                            {expired ? 'Expired' : value}
                        </Badge>
                        {record.account_expiration_date && (
                            <p className="text-xs text-gray-500">
                                Expires: {formatDate(record.account_expiration_date)}
                            </p>
                        )}
                    </div>
                );
            },
        },
        {
            key: 'actions',
            title: 'Actions',
            render: (_: any, record: Organization) => (
                <div className="flex items-center space-x-2">
                    <button
                        onClick={() => handleOpenModal(record)}
                        className="p-1 text-gray-600 hover:text-blue-600 transition-colors"
                        title="Edit"
                    >
                        <Edit2 className="h-4 w-4"/>
                    </button>
                    <button
                        onClick={() => handleDelete(record)}
                        className="p-1 text-gray-600 hover:text-red-600 transition-colors"
                        title="Delete"
                    >
                        <Trash2 className="h-4 w-4"/>
                    </button>
                </div>
            ),
        },
    ];

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Manage {labels.organizationPlural}</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        {accountType === 'business'
                            ? 'Create and manage business tier 1 organizations'
                            : 'Create and manage educational districts'}
                    </p>
                </div>
                <Button onClick={() => handleOpenModal()} className="flex items-center space-x-2">
                    <Plus className="h-4 w-4"/>
                    <span>Add {labels.organizationSingular}</span>
                </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total {labels.organizationPlural}</p>
                            <p className="text-2xl font-bold text-gray-900">{filteredCount}</p>
                        </div>
                        <Building2 className="h-8 w-8 text-blue-500"/>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Active {labels.organizationPlural}</p>
                            <p className="text-2xl font-bold text-green-600">{filteredCount - expiredCount}</p>
                        </div>
                        <Users className="h-8 w-8 text-green-500"/>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Expired</p>
                            <p className="text-2xl font-bold text-red-600">{expiredCount}</p>
                        </div>
                        <AlertTriangle className="h-8 w-8 text-red-500"/>
                    </div>
                </div>
            </div>

            {/* Search and Filter Bar */}
            <FilterBar
                searchValue={searchTerm}
                onSearchChange={setSearchTerm}
                searchPlaceholder={`Search ${labels.organizationPlural.toLowerCase()}...`}
                filters={buildOrganizationFilters({
                    labels,
                    filters,
                    onChange: (patch) => setFilters(prev => ({...prev, ...patch})),
                    schoolFilter,
                    onSchoolFilterChange: setSchoolFilter,
                    accountOptions: accountsForFilter.map(a => ({value: a.id, label: a.name})),
                })}
            />

            {/* Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <LoadingSpinner size="lg"/>
                    </div>
                ) : organizations && organizations.length > 0 ? (
                    <Table
                        columns={columns}
                        data={organizations}
                        loading={isLoading}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                    />
                ) : (
                    <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                            <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4"/>
                            <p className="text-gray-500">No {labels.organizationPlural.toLowerCase()} found</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Create/Edit Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={editingOrganization ? `Edit ${labels.organizationSingular}` : `Add ${labels.organizationSingular}`}
                size="lg"
            >
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {labels.organizationSingular} Name *
                            </label>
                            <Input
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                placeholder={`Enter ${labels.organizationSingular.toLowerCase()} name`}
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                State *
                            </label>
                            <Select
                                value={formData.state}
                                onChange={(value) => handleInputChange('state', value)}
                                options={US_STATES.map(state => ({value: state, label: state}))}
                                placeholder="Select state"
                                required
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Administrator Name *
                            </label>
                            <Input
                                value={formData.administrator_name}
                                onChange={(e) => handleInputChange('administrator_name', e.target.value)}
                                placeholder="Enter administrator name"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Administrator Email *
                            </label>
                            <Input
                                type="email"
                                value={formData.administrator_email}
                                onChange={(e) => handleInputChange('administrator_email', e.target.value)}
                                placeholder="Enter administrator email"
                                required
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Administrator Phone
                            </label>
                            <Input
                                type="tel"
                                value={formData.administrator_phone}
                                onChange={(e) => handleInputChange('administrator_phone', e.target.value)}
                                placeholder="Enter phone number"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Active Activation Date
                            </label>
                            <Input
                                type="date"
                                value={formData.active_activation_date}
                                onChange={(e) => handleInputChange('active_activation_date', e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Account Expiration Date
                            </label>
                            <Input
                                type="date"
                                value={formData.account_expiration_date}
                                onChange={(e) => handleInputChange('account_expiration_date', e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={handleCloseModal}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            isLoading={createOrganizationMutation.isPending || updateOrganizationMutation.isPending}
                        >
                            {editingOrganization ? `Update ${labels.organizationSingular}` : `Create ${labels.organizationSingular}`}
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};
